import { v1Tasks, type V1Task, type V1TaskId } from '../../model/delivery'
import { Btn, Cta, Footer, Icon, ItemRow, Strip, Tick, V1Screen, WaitCta } from '../kit'
import { C } from '../style'
import { cropName, tr } from '../text'
import { buyableCultivations } from './cultivations'
import { StepHeader } from './shared'
import { useV1 } from './ctx'
import { CROP_ICON, TASK_ICON, TASK_NAME, TASK_SHORT } from './taskNames'

/**
 * After the crop: the delivery's tasks, done in any order (the two questions, moisture, bags,
 * seal, bank account). Each opens its screen and comes back here; the slip opens once every
 * required task is done. Once he has agreed the slip, nothing here changes any more.
 */
export function TasksScreen({
  onBack,
  onCrop,
  onOpen,
  onSlip,
}: {
  onBack: () => void
  /** Back to the field and crop screen to pick another cultivation. */
  onCrop: () => void
  onOpen: (t: V1TaskId) => void
  onSlip: () => void
}) {
  const { d, roster } = useV1()
  const field = buyableCultivations(roster, d.farmer.id).find((c) => c.id === d.cultivation_id)
  const place = field ? [field.farm_name, field.plot_label].filter(Boolean).join(' · ') : ''
  const tasks = v1Tasks(d)
  const left = tasks.filter((t) => t.required && !t.done)
  const agreed = d.slip.farmer_agreed_at !== null

  const status = (t: V1Task): string => {
    if (t.id === 'bags' && d.bags.length > 0) {
      const n = tr('gate.v1.bags', { count: d.bags.length })
      return t.done ? n : `${n} · ${tr('gate.v1.task.weighLeft')}`
    }
    if (
      t.id === 'bank' &&
      d.farmer.bank_later &&
      !d.farmer.bank_verified &&
      !d.farmer.bank_pending_last4
    )
      return tr('gate.v1.bank.later')
    if (t.done) return tr('gate.v1.task.done')
    if (t.id === 'seal' && !t.required) return tr('gate.v1.task.sealNotNeeded')
    return tr(t.required ? 'gate.v1.task.toDo' : 'gate.v1.task.optional')
  }

  return (
    <V1Screen
      main={{ gap: 8 }}
      header={
        <StepHeader step="details" icon="ticks" title={tr('gate.v1.task.screen')} onBack={onBack} />
      }
      footer={
        <Footer>
          {agreed ? (
            <Cta label={tr('gate.v1.next')} icon="next" onClick={onSlip} />
          ) : left.length === 0 ? (
            <Cta label={tr('gate.v1.task.showSlip')} icon="slip" onClick={onSlip} />
          ) : (
            <WaitCta>
              {tr('gate.v1.home.left', {
                tasks: left.map((t) => tr(TASK_SHORT[t.id])).join(', '),
              })}
            </WaitCta>
          )}
        </Footer>
      }
    >
      {agreed && <Strip icon="lock">{tr('gate.v1.task.locked')}</Strip>}
      {/* The field and crop picked first: a summary block, not a task (no tick, no arrow). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 10,
          borderRadius: 12,
          background: C.sunk,
          flexShrink: 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 52,
            height: 52,
            flexShrink: 0,
            borderRadius: 8,
            background: C.photo,
            color: C.photoInk,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={CROP_ICON[d.crop_id ?? ''] ?? 'sack'} size={30} sw={1.4} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flexGrow: 1 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.muted,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            {tr('gate.v1.crop.title')}
          </span>
          <b style={{ fontSize: 19, lineHeight: 1.25 }}>
            {cropName(roster.config.crops, d.crop_id)}
            {field?.variety_name ? (
              <span style={{ fontWeight: 500, color: C.muted }}> · {field.variety_name}</span>
            ) : null}
          </b>
          {place && <span style={{ fontSize: 15, color: C.muted }}>{place}</span>}
        </span>
        {!agreed && (
          <div style={{ flex: '0 0 auto', display: 'flex' }}>
            <Btn icon="pencil" onClick={onCrop}>
              {tr('gate.v1.change')}
            </Btn>
          </div>
        )}
      </div>
      <h2 style={{ margin: '6px 0 0', fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
        {tr('gate.v1.task.title')}
      </h2>
      {tasks.map((t) => {
        const muted = !t.required && !t.done
        return (
          <ItemRow
            key={t.id}
            onClick={agreed ? undefined : () => onOpen(t.id)}
            border={t.required && !t.done ? C.acc : C.rule}
            borderWidth={t.required && !t.done ? 2 : 1.5}
            padding="10px 12px"
            lead={
              <span style={{ color: muted ? C.muted : C.acc, display: 'flex' }}>
                <Icon name={TASK_ICON[t.id]} size={28} />
              </span>
            }
            titleSize={18}
            title={tr(TASK_NAME[t.id])}
            line={<span style={{ color: C.muted }}>{status(t)}</span>}
            right={
              t.done ? (
                <Tick />
              ) : agreed ? undefined : (
                <span style={{ color: C.acc, display: 'flex' }}>
                  <Icon name="next" size={24} sw={2.4} />
                </span>
              )
            }
          />
        )
      })}
    </V1Screen>
  )
}
